import React, { useMemo } from 'react';
import * as TablerExport from '@tabler/icons-react';
import { cventIconsAltIcons, cventIconsMap, customIconsOverrides, CustomIconSlotName } from './iconConstants';
import { useAppContext } from '@/context/AppContext/AppContext';

export type IconName = Exclude<keyof typeof TablerExport, 'createReactComponent'> | CustomIconSlotName;
export interface CustomIconProps extends Omit<TablerExport.IconProps, 'name'> {
  name: IconName;
}

const DEFAULT_ICON_SIZE = 24;

const Icon: React.FC<CustomIconProps> = ({ name, size, width, height, ...restProps }) => {
  const { theme, logger } = useAppContext();

  const sizeProps = {
    width: width || size || DEFAULT_ICON_SIZE,
    height: height || size || DEFAULT_ICON_SIZE
  };

  const IconComponent = useMemo(() => {
    const component = customIconsOverrides[theme || '']?.[name];
    if (component) {
      return component;
    }
    const iconsMap = { ...cventIconsAltIcons, ...cventIconsMap };
    // Check if cvent theme is set and icon exists in cvent collection
    if (theme === 'cvent' && iconsMap[name as string]) {
      return iconsMap[name as string];
    }

    // Fallback to tabler icon
    const TablerIcon = TablerExport.icons[name];
    if (TablerIcon) {
      return TablerIcon;
    }

    logger.error('Icon not found in icon libraries', { iconName: name });
    return null;
  }, [name, theme, logger]);

  if (!IconComponent) {
    return null;
  }

  return <IconComponent {...restProps} {...sizeProps} />;
};

export default Icon;
